/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable class-methods-use-this */
import Config from "@/config/Config";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Registration } from "@models/Registration";
import { RegistrationTransferUpdateRequest } from "@models/RegistrationTransferUpdateRequest";
// eslint-disable-next-line object-curly-newline
import {
  getFirestore,
  collection,
  where,
  query,
  getDocs,
  getDoc,
  doc,
  getCountFromServer,
  orderBy,
  startAfter,
  limit,
  setDoc,
  collectionGroup,
  and,
  or,
} from "firebase/firestore";

class RegistrationDataService {
  eventCollectionName = "events";

  collectionName = "registrations";

  warmup(): void {
    const functions = getFunctions();
    const cancelRegistrationAssignmentFunc = httpsCallable(
      functions,
      Config.functions.CancelRegistrationAssignment
    );

    cancelRegistrationAssignmentFunc({
      warmup: true,
    });

    const claimRegistrationAssignmentFunc = httpsCallable(functions, Config.functions.RedeemRegistration);
    claimRegistrationAssignmentFunc({
      warmup: true,
    });

    const assignRegistrationFunc = httpsCallable(functions, Config.functions.AssignRegistration);
    assignRegistrationFunc({
      warmup: true,
    });

    const adminUpdateRegistrationTransferFunc = httpsCallable(functions, Config.functions.AdminUpdateRegistrationTransfer);
    adminUpdateRegistrationTransferFunc({
      warmup: true,
    });
  }
  async getByOwner(ownerId: string): Promise<Registration[]> {
    const db = getFirestore();

    const snapshotRegistrations = await getDocs(query(
      collectionGroup(db, this.collectionName),
      where("ownerId", "==", ownerId),
      where("transferStatus", "in", ["purchased", "transferred"])
    ));

    const results: Registration[] = [];

    snapshotRegistrations.forEach((item) => {
      results.push({ ...item.data(), id: item.id } as Registration);
    });

    return results;
  }

  async getByEventOwner(eventId: string, ownerId: string): Promise<Registration[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const snapshotRegistrations = await getDocs(query(
      collection(eventDoc, this.collectionName),
      where("ownerId", "==", ownerId),
      where("transferStatus", "in", ["purchased", "transferred"])
    ));

    const results: Registration[] = [];

    snapshotRegistrations.forEach((item) => {
      results.push({ ...item.data(), id: item.id } as Registration);
    });

    return results;
  }

  async getTransferRegistrations(eventId: string): Promise<Registration[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const registrations = query(
      collection(eventDoc, this.collectionName),
      // eslint-disable-next-line comma-dangle
      where("transferStatus", "!=", "purchased")
    );

    const data = await getDocs(registrations);
    const results: Registration[] = [];

    data.forEach((item) => {
      results.push({ ...item.data(), id: item.id } as Registration);
    });
    return results;
  }

  async getByTransactionId(eventId: string, transactionId: string): Promise<Registration[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const registrations = query(
      collection(eventDoc, this.collectionName),
      // eslint-disable-next-line comma-dangle
      where("transactionIds", "array-contains", `${transactionId}`)
    );

    const data = await getDocs(registrations);
    const results: Registration[] = [];

    data.forEach((item) => {
      results.push({ ...item.data(), id: item.id } as Registration);
    });
    return results;
  }

  async getAll(eventId: string): Promise<Registration[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const registrations = query(collection(eventDoc, this.collectionName));

    const data = await getDocs(registrations);
    const results: Registration[] = [];

    data.forEach((item) => {
      results.push({ ...item.data(), id: item.id } as Registration);
    });
    return results;
  }

  async getByTransferCode(eventId: string, transferCode: string): Promise<Registration[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const registrations = query(
      collection(eventDoc, this.collectionName),
      // eslint-disable-next-line comma-dangle
      where("transferCode", "==", `${transferCode}`)
    );

    const data = await getDocs(registrations);
    const results: Registration[] = [];
    if (data.size === 0) {
      return results;
    }

    data.forEach((item) => {
      results.push({ ...item.data(), id: item.id } as Registration);
    });
    return results;
  }
  
  async getByUser(userId: string): Promise<Registration[]> {
    const db = getFirestore();

    const queryRegistrations = await getDocs(query(
      collectionGroup(db, this.collectionName),
      or(
          where("assignedToId", "==", userId),
          where("ownerId", "==", userId)
        )
    ));

    const results: Registration[] = [];
    queryRegistrations.docs.forEach((item) => {
      const registration = { id: item.id, ...item.data() } as Registration;
      if(!registration.transferStatus || registration.transferStatus === "purchased" || registration.transferStatus === "transferred") {
        results.push(registration);
      }
    });
    
    return results;
  }

  async getByEventUser(eventId: string, userId: string): Promise<Registration[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const queryRegistrations = await getDocs(query(
      collection(eventDoc, this.collectionName),  
        or(
          where("assignedToId", "==", userId),
          where("ownerId", "==", userId)
        )
    ));

    const results: Registration[] = [];
    queryRegistrations.docs.forEach((item) => {
      const registration = { id: item.id, ...item.data() } as Registration;
      if(!registration.transferStatus || registration.transferStatus === "purchased" || registration.transferStatus === "transferred") {
        results.push(registration);
      }
    });
    
    return results;
  }

  async getByAssignToId(assignToId: string): Promise<Registration[]> {
    const db = getFirestore();

    const queryRegistrations = await getDocs(query(
      collectionGroup(db, this.collectionName),
      where("assignedToId", "==", assignToId),
      where("transferStatus", "in", ["purchased","transferred"])
    ));

    const results: Registration[] = [];
    queryRegistrations.docs.forEach((item) => {
      results.push({ id: item.id, ...item.data() } as Registration);
    });
    
    return results;
  }

  async getByEventAssignToId(eventId: string, assignToId: string): Promise<Registration[]> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const queryRegistrations = await getDocs(query(
      collection(eventDoc, this.collectionName),
      where("assignedToId", "==", assignToId),
      where("transferStatus", "in", ["purchased","transferred"])
    ));

    const results: Registration[] = [];
    queryRegistrations.docs.forEach((item) => {
      results.push({ id: item.id, ...item.data() } as Registration);
    });
    
    return results;
  }

  async getCountByEvent(eventId: string): Promise<number> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);
    const snapshot = await getCountFromServer(collection(eventDoc, this.collectionName));
    return snapshot.data().count;
  }

  async getByEvent(
    eventId: string,
    handleDataReceived: (registrations: Registration[]) => void
  ): Promise<void> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);
    let hasData = true;
    let lastVisible = null;
    while (hasData) {
      const registrationQuery = (
        lastVisible
          ? query(
              collection(eventDoc, this.collectionName),
              orderBy("participantName"),
              startAfter(lastVisible),
              limit(50)
            )
          : query(collection(eventDoc, this.collectionName), orderBy("participantName"), limit(50))
      ) as any;

      const data = await getDocs(registrationQuery);
      lastVisible = data.docs[data.docs.length - 1];
      const results: Registration[] = [];

      data.forEach((item) => {
        const itemData = item.data() as Registration;
        results.push({ ...itemData, id: item.id } as Registration);
      });
      handleDataReceived(results);
      hasData = results.length > 0;
    }
  }

  async get(eventId: string, id: string): Promise<Registration> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, eventId);

    const registrationRef = doc(
      collection(eventDoc, this.collectionName),
      // eslint-disable-next-line comma-dangle
      id
    );

    const data = await getDoc(registrationRef);
    return {
      ...data.data(),
      id: data.id,
    } as Registration;
  }

  async setTransferData(registration: Registration, newName: string): Promise<Registration> {
    const db = getFirestore();
    const eventDoc = doc(db, this.eventCollectionName, registration.eventId);
    const registrationDoc = doc(eventDoc, this.collectionName, registration.id as string);
    const updatedData = {
      transferToName: newName
    } as Partial<unknown>;
    await setDoc(registrationDoc, updatedData, { merge: true });
    return registration;
  }

  async assign(registration: Registration, name: string): Promise<void> {
    const functions = getFunctions();
    const assignRegistrationFunc = httpsCallable(functions, Config.functions.AssignRegistration);
    const result = (await assignRegistrationFunc({
      registration,
      name,
    })) as any;

    if (result?.data?.error) {
      throw result;
    }
  }

  async adminUpdateRegistrationTransfer(
    request: RegistrationTransferUpdateRequest
  ): Promise<RegistrationTransferUpdateRequest> {
    const functions = getFunctions();
    const adminUpdateRegistrationTransferFunc = httpsCallable(functions, Config.functions.AdminUpdateRegistrationTransfer);
    const result = (await adminUpdateRegistrationTransferFunc(request)) as any;

    if (result?.data?.error) {
      throw result;
    }

    return result.data;
  }

  async transferRegistration(
    eventId: string,
    transferCode: string,
    userId?: string
  ): Promise<void> {
    const functions = getFunctions();
    const transferRegistrationFunc = httpsCallable(functions, Config.functions.TransferRegistration);
    const result = (await transferRegistrationFunc({
      eventId,
      transferCode,
      userId,
    })) as any;

    if (result?.data?.error) {
      throw result;
    }
  }

  async sendTransferMessage(
    registration: Registration
  ): Promise<void> {
    const functions = getFunctions();
    const sendTransferMessageFunc = httpsCallable(functions, Config.functions.AdminSendTransferMessage);
    const result = (await sendTransferMessageFunc(registration)) as any;

    if (result?.data?.error) {
      throw result;
    }
  }
  
  async cancelTransfer(registration: Registration): Promise<void> {
    const functions = getFunctions();
    const cancelTransferFunc = httpsCallable(functions, Config.functions.CancelTransfer);
    const result = (await cancelTransferFunc({
      registration,
    })) as any;

    if (result?.data?.error) {
      throw result;
    }
  }

  async cancelAssignment(registration: Registration): Promise<void> {
    const functions = getFunctions();
    const cancelRegistrationAssignmentFunc = httpsCallable(
      functions,
      Config.functions.CancelRegistrationAssignment
    );
    const result = (await cancelRegistrationAssignmentFunc({
      registration,
    })) as any;

    if (result?.data?.error) {
      throw result;
    }
  }

  async claimAssignment(eventId: string, redemptionCode: string, userId?: string): Promise<void> {
    const functions = getFunctions();
    const claimRegistrationAssignmentFunc = httpsCallable(functions, Config.functions.RedeemRegistration);
    const request = {
      eventId,
      redemptionCode,
    } as any;

    if (userId) {
      request.userId = userId;
    }

    const result = (await claimRegistrationAssignmentFunc(request)) as any;

    if (result?.data?.error) {
      throw result;
    }
  }
}

export default new RegistrationDataService();
